import { api } from '@/lib/api'
import type { ReportDef, ReportResultTable } from '@/components/ReportRunner'

const yearNow = () => String(new Date().getFullYear())
const single = (obj: Record<string, unknown> | null | undefined): Record<string, unknown>[] => (obj ? [obj] : [])

const QUARTER_OPTS = [
  { value: '', label: 'Full year' },
  { value: '1', label: 'Q1' },
  { value: '2', label: 'Q2' },
  { value: '3', label: 'Q3' },
  { value: '4', label: 'Q4' },
]

export const PAYROLL_REPORTS: ReportDef[] = [
  {
    slug: 'tax_liability',
    name: 'Tax Liability',
    description: 'Federal + state + local employer and employee tax totals by quarter, from posted pay runs.',
    params: [
      { key: 'tax_year', label: 'Tax year', type: 'number', required: true, defaultValue: yearNow() },
      { key: 'quarter', label: 'Quarter', type: 'select', options: QUARTER_OPTS },
    ],
    async run(p) {
      const qs = new URLSearchParams({ tax_year: p.tax_year })
      if (p.quarter) qs.set('quarter', p.quarter)
      const r = await api.get<{ data: { by_run?: Record<string, unknown>[]; totals?: Record<string, unknown> } }>(`/api/payroll/reports/tax-liability?${qs.toString()}`)
      const d = r.data || {}
      const tables: ReportResultTable[] = []
      if (d.by_run) tables.push({ title: 'By pay run', rows: d.by_run })
      if (d.totals) tables.push({ title: 'Totals', rows: single(d.totals) })
      return tables
    },
  },
  {
    slug: 'labor_cost',
    name: 'Labor Cost',
    description: 'Earnings + employer taxes + benefits grouped by your chosen dimension.',
    params: [
      { key: 'start_date', label: 'Start date', type: 'date', required: true },
      { key: 'end_date', label: 'End date', type: 'date', required: true },
      {
        key: 'group_by', label: 'Group by', type: 'select', options: [
          { value: 'cost_center', label: 'Cost center' },
          { value: 'department', label: 'Department' },
          { value: 'classification', label: 'Classification' },
          { value: 'employment_type', label: 'Employment type' },
        ],
      },
    ],
    async run(p) {
      const qs = new URLSearchParams({ start_date: p.start_date, end_date: p.end_date, group_by: p.group_by || 'cost_center' })
      const r = await api.get<{ data: { rows?: Record<string, unknown>[] } }>(`/api/payroll/reports/labor-cost?${qs.toString()}`)
      return [{ rows: r.data?.rows ?? [] }]
    },
  },
  {
    slug: 'headcount',
    name: 'Headcount',
    description: 'Active / on-leave / terminated counts by status, classification, and employment type.',
    async run() {
      const r = await api.get<{ data: { totals?: Record<string, unknown>; by_status?: Record<string, unknown>[]; by_classification?: Record<string, unknown>[]; by_employment_type?: Record<string, unknown>[] } }>('/api/payroll/reports/headcount')
      const d = r.data || {}
      return [
        { title: 'Totals', rows: single(d.totals) },
        { title: 'By status', rows: d.by_status ?? [] },
        { title: 'By classification', rows: d.by_classification ?? [] },
        { title: 'By employment type', rows: d.by_employment_type ?? [] },
      ]
    },
  },
  {
    slug: 'overtime',
    name: 'Overtime Analysis',
    description: 'Overtime and double-time hours by employee for a date range.',
    params: [
      { key: 'start_date', label: 'Start date', type: 'date', required: true },
      { key: 'end_date', label: 'End date', type: 'date', required: true },
    ],
    async run(p) {
      const qs = new URLSearchParams({ start_date: p.start_date, end_date: p.end_date })
      const r = await api.get<{ data: { rows?: Record<string, unknown>[] } }>(`/api/payroll/reports/overtime?${qs.toString()}`)
      return [{ rows: r.data?.rows ?? [] }]
    },
  },
  {
    slug: 'eeo_1',
    name: 'EEO-1 Component 1',
    description: 'Active workforce counts by EEO job category, sex, and race / ethnicity self-identification.',
    async run() {
      const r = await api.get<{ data: { by_job_category?: Record<string, unknown>[]; by_ethnicity?: Record<string, unknown>[]; totals?: Record<string, unknown> } }>('/api/payroll/reports/eeo-1')
      const d = r.data || {}
      return [
        { title: 'By job category', rows: d.by_job_category ?? [] },
        { title: 'By race / ethnicity', rows: d.by_ethnicity ?? [] },
        { title: 'Coverage', rows: single(d.totals) },
      ]
    },
  },
  {
    slug: 'aca_1095c',
    name: 'ACA 1095-C Summary',
    description: 'Per-employee full-time months, offer codes, and affordability safe harbors for a year.',
    params: [
      { key: 'tax_year', label: 'Tax year', type: 'number', required: true, defaultValue: yearNow() },
    ],
    async run(p) {
      const r = await api.get<{ data: { rows?: Record<string, unknown>[] } }>(`/api/payroll/reports/aca-1095c?tax_year=${encodeURIComponent(p.tax_year)}`)
      return [{ rows: r.data?.rows ?? [] }]
    },
  },
  {
    slug: 'certified_payroll',
    name: 'Certified Payroll (WH-347)',
    description: 'Davis-Bacon weekly certified payroll reports by project and week.',
    params: [
      { key: 'week_ending', label: 'Week ending', type: 'date' },
      { key: 'project_code', label: 'Project code', type: 'text', placeholder: 'Optional' },
    ],
    async run(p) {
      const qs = new URLSearchParams()
      if (p.week_ending) qs.set('week_ending', p.week_ending)
      if (p.project_code) qs.set('project_code', p.project_code)
      const r = await api.get<{ data: { rows?: Record<string, unknown>[] } }>(`/api/payroll/reports/certified-payroll?${qs.toString()}`)
      return [{ rows: r.data?.rows ?? [] }]
    },
  },
]
